import React from 'react';
import { Link } from 'react-router-dom';
import PlatformLayout from '../PlatformLayout';
import { ICONS, Arrow, IconKey } from '../icons';

type Row = {
  icon: IconKey; kicker: string; title: string;
  body: string; points: string[];
};

const ROWS: Row[] = [
  { icon: 'donations', kicker: 'Collect', title: 'Donations that go straight to you',
    body: 'Take gifts from your first day. SGOKit runs on Stripe Connect, so contributions settle directly into your organization’s account — we never hold your money. Every gift is receipted and tracked against the donor’s S25F credit.',
    points: ['Per-tenant Stripe Express onboarding', 'Pass-through processing at non-profit rates', 'Automatic donor receipts and running credit totals', 'One dashboard for gifts, applications, and awards'] },
  { icon: 'applications', kicker: 'Intake', title: 'Applications on your terms',
    body: 'Open scholarship applications with the eligibility criteria you decide — household income, geography, school type, grade level, or rules of your own. Families apply once; you review in one place.',
    points: ['Configurable eligibility criteria', 'Income verification against the 3× local-median cap', 'Document upload and status tracking', 'Applicant communication built in'] },
  { icon: 'scoring', kicker: 'Select', title: 'Objective, quantitative scoring',
    body: 'Selection you can defend. Build a scoring rubric, weight what matters, and rank applicants on the numbers — so awards are consistent, repeatable, and free of guesswork or bias claims.',
    points: ['Custom weighted rubrics', 'Blind-review options', 'Reviewer assignment and reconciliation', 'Auditable score history per applicant'] },
  { icon: 'disbursement', kicker: 'Award', title: 'Disbursement, tracked end to end',
    body: 'Turn a decision into a paid scholarship. Award to families or schools, schedule payments, and follow every dollar from the donor’s gift to the scholar’s tuition.',
    points: ['Award letters and acceptance', 'Payments to families or schools', 'Full gift-to-grant money trail', 'Reconciliation and reporting'] },
  { icon: 'compliance', kicker: 'Comply', title: 'S25F guardrails, baked in',
    body: 'The federal credit comes with rules. SGOKit checks them as you work: contributions only count where the state has opted in, scholarships only go to families under the income cap, and donors can’t stack a federal credit on top of a state one.',
    points: ['State opt-in enforcement', 'Income-cap eligibility checks', 'No double-dip credit validation', 'Records ready for approval and audit'] },
];

export default function Platform() {
  return (
    <PlatformLayout>
      <section className="sgk-section sgk-section--tight">
        <div className="sgk-wrap">
          <div className="sgk-head sgk-reveal">
            <span className="sgk-eyebrow">The platform</span>
            <h2>The whole scholarship lifecycle, <em>in one kit.</em></h2>
            <p>Donations, applications, scoring, disbursement, and compliance — the
              operational machinery of a scholarship granting organization, on a single
              platform built for Section 25F.</p>
          </div>
        </div>
      </section>

      <section className="sgk-section" style={{ paddingTop: 0 }}>
        <div className="sgk-wrap">
          {ROWS.map((r, i) => {
            const Icon = ICONS[r.icon];
            return (
              <div className={`sgk-feature sgk-reveal${i % 2 ? ' sgk-feature--flip' : ''}`} key={r.title}>
                <div>
                  <span className="sgk-eyebrow">{r.kicker}</span>
                  <h3>{r.title}</h3>
                  <p style={{ marginTop: 14 }}>{r.body}</p>
                  <ul>{r.points.map((p) => <li key={p}>{p}</li>)}</ul>
                </div>
                <div className="sgk-feature__media">
                  <div className="sgk-module__icon" style={{ width: 96, height: 96, borderRadius: 20 }}>
                    <Icon size={52} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="sgk-cta">
        <div className="sgk-wrap sgk-cta__grid">
          <div>
            <h2>Run every step in one place.</h2>
            <p>Start taking donations today; grow into the full toolkit as it ships.</p>
          </div>
          <Link to="/signup" className="sgk-btn sgk-btn--navy" data-testid="platform-cta-start-button">
            Start an SGO <Arrow />
          </Link>
        </div>
      </section>
    </PlatformLayout>
  );
}
